import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Building2, Coins, CalendarClock, Link2, Link2Off, ArrowLeft } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import SimpleSelect from '@/components/ui/SimpleSelect';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';
import { tenantCreditPlanService } from '@/services/tenant-credit-plan.service';
import { creditPlanService } from '@/services/credit-plan.service';
import { ADMIN_CREDIT_PLANS } from '@/constants/constant-routes';
import type { TenantWithPlan } from '@/types/tenant-credit-plan.types';
import type { CreditPlan } from '@/types/credit-plan.types';

const ITEMS_PER_PAGE = 6;

const errMsg = (error: any, fallback: string) =>
  error?.response?.data?.error?.error_description ||
  error?.response?.data?.error?.message ||
  error?.message ||
  fallback;

const fmtDate = (d?: string) => { try { return d ? format(new Date(d), 'PP') : '—'; } catch { return '—'; } };

export default function TenantCreditPlans() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Assign modal state
  const [assignTenant, setAssignTenant] = useState<TenantWithPlan | null>(null);
  const [selectedPlanKey, setSelectedPlanKey] = useState('');
  // Unassign confirm state
  const [unassignTenant, setUnassignTenant] = useState<TenantWithPlan | null>(null);

  const { data: tenantsResp, isLoading } = useQuery({
    queryKey: ['tenant-credit-plans', searchTerm],
    queryFn: () => tenantCreditPlanService.list(searchTerm),
  });

  // All plans — for the assign dropdown and to resolve a plan_key → plan name for display.
  const { data: plansResp } = useQuery({
    queryKey: ['credit-plans', ''],
    queryFn: () => creditPlanService.list(),
  });

  const tenants: TenantWithPlan[] = useMemo(() => {
    const raw = (tenantsResp as any)?.data;
    return Array.isArray(raw) ? raw : [];
  }, [tenantsResp]);

  const plans: CreditPlan[] = useMemo(() => {
    const raw = (plansResp as any)?.data;
    return Array.isArray(raw) ? raw : [];
  }, [plansResp]);

  const planName = (plan_key?: string) => plans.find((p) => p.plan_key === plan_key)?.plan_name || plan_key || '—';
  const planOptions = useMemo(
    () => plans.map((p) => ({ label: p.plan_name, value: p.plan_key })),
    [plans],
  );

  const hasActiveSearch = searchTerm.trim().length > 0;
  const totalPages = Math.max(1, Math.ceil(tenants.length / ITEMS_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const pagedTenants = useMemo(
    () => tenants.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [tenants, page],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenant-credit-plans'] });

  const assignMutation = useMutation({
    mutationFn: (payload: { tenant_key: string; plan_key: string }) => tenantCreditPlanService.assign(payload),
    onSuccess: () => { toast.success('Plan assigned.'); invalidate(); closeAssign(); },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to assign plan.')),
  });

  const unassignMutation = useMutation({
    mutationFn: (tenant_key: string) => tenantCreditPlanService.unassign(tenant_key),
    onSuccess: () => { toast.success('Plan unassigned.'); invalidate(); setUnassignTenant(null); },
    onError: (error: any) => toast.error(errMsg(error, 'Failed to unassign plan.')),
  });

  const openAssign = (tenant: TenantWithPlan) => {
    setAssignTenant(tenant);
    setSelectedPlanKey(tenant.credit_plan?.plan_key || '');
  };
  const closeAssign = () => { setAssignTenant(null); setSelectedPlanKey(''); };

  const confirmAssign = () => {
    if (!assignTenant) return;
    if (!selectedPlanKey) { toast.error('Please select a plan.'); return; }
    assignMutation.mutate({ tenant_key: assignTenant.tenant_key, plan_key: selectedPlanKey });
  };

  const statusBadge = (status: string) => {
    const s = (status || '').toUpperCase();
    const cls = s === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700'
      : s === 'SUSPENDED' ? 'bg-red-50 text-red-700'
      : 'bg-gray-100 text-gray-500';
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{s || '—'}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header — violet/purple theme */}
      <div className="bg-gradient-to-r from-brand-violet-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <button onClick={() => navigate(ADMIN_CREDIT_PLANS)} className="inline-flex items-center gap-1 text-brand-violet-100 hover:text-white mb-3 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Credit Plans
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg"><Building2 className="w-7 h-7" /></div>
          <div>
            <h1 className="text-2xl font-bold">Tenant Plans</h1>
            <p className="text-brand-violet-100">Assign a credit plan to each tenant (one plan per tenant).</p>
          </div>
        </div>
      </div>

      {/* Search */}
      {(tenants.length > 0 || hasActiveSearch) && (
        <div className="card">
          <SearchInputDebounced
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder="Search by tenant name or key…"
            debounceDelay={DEFAULT_DEBOUNCE_DELAY}
            className="w-full"
          />
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card">
              <SkeletonLoader type="title" className="h-6 w-48 mb-3" />
              <SkeletonLoader type="text" className="h-4 w-full mb-2" />
              <SkeletonLoader type="text" className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : tenants.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-brand-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-brand-violet-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {hasActiveSearch ? 'No tenants match your search' : 'No tenants found'}
          </h3>
          <p className="text-gray-600">
            {hasActiveSearch ? 'Try a different search term or clear the filter.' : 'Tenants will appear here once created.'}
          </p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pagedTenants.map((tenant) => {
            const assigned = tenant.credit_plan;
            return (
              <div
                key={tenant.tenant_key}
                className="bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-lg hover:border-brand-violet-300 transition-all"
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">{tenant.name || tenant.tenant_key}</h3>
                      <p className="text-xs text-gray-400 truncate">{tenant.tenant_key}</p>
                    </div>
                    {statusBadge(tenant.status)}
                  </div>

                  {assigned ? (
                    <div className="rounded-lg border border-brand-violet-100 bg-brand-violet-50/60 p-4 mb-4">
                      <div className="flex items-center gap-1.5 text-brand-violet-700 mb-2">
                        <Coins className="w-4 h-4" />
                        <span className="text-sm font-semibold">{planName(assigned.plan_key)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <CalendarClock className="w-3.5 h-3.5 text-gray-400" />
                        <span>{fmtDate(assigned.start_date)} → {fmtDate(assigned.end_date)}</span>
                      </div>
                      {!!assigned.rollover_credits && assigned.rollover_credits > 0 && (
                        <div className="flex items-start gap-2 text-xs text-emerald-700 mt-2">
                          <Coins className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          <span>
                            <span className="font-semibold">+{assigned.rollover_credits.toLocaleString()} rolled over</span>
                            {assigned.rollover_from_plan_key ? ` from ${planName(assigned.rollover_from_plan_key)}` : ''}
                            {assigned.rollover_at ? ` · ${fmtDate(assigned.rollover_at)}` : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 mb-4 text-center">
                      <p className="text-sm text-gray-500">No plan assigned</p>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => openAssign(tenant)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-violet-700 bg-brand-violet-50 hover:bg-brand-violet-100 rounded-lg"
                    >
                      <Link2 className="w-4 h-4" /> {assigned ? 'Change plan' : 'Assign plan'}
                    </button>
                    {assigned && (
                      <button
                        onClick={() => setUnassignTenant(tenant)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg"
                      >
                        <Link2Off className="w-4 h-4" /> Unassign
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={tenants.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
            showItemsPerPage={false}
          />
        </div>
        </>
      )}

      {/* Assign / change plan modal */}
      <Modal
        isOpen={!!assignTenant}
        onClose={closeAssign}
        title={assignTenant?.credit_plan ? 'Change Plan' : 'Assign Plan'}
        subtitle={assignTenant ? (assignTenant.name || assignTenant.tenant_key) : ''}
        size="md"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button onClick={closeAssign} disabled={assignMutation.isPending}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">Cancel</button>
            <button onClick={confirmAssign} disabled={assignMutation.isPending || !selectedPlanKey}
              className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-brand-violet-600 to-brand-purple-600 text-white rounded-lg font-semibold hover:from-brand-violet-700 hover:to-brand-purple-700 disabled:opacity-50 disabled:cursor-not-allowed">
              <Link2 className="w-4 h-4" /> {assignMutation.isPending ? 'Assigning…' : 'Assign'}
            </button>
          </div>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
          <SimpleSelect
            value={selectedPlanKey}
            onChange={(v) => setSelectedPlanKey((v as string) || '')}
            options={planOptions}
            placeholder={planOptions.length ? 'Select a plan' : 'No plans available'}
          />
          <p className="text-xs text-gray-500 mt-2">
            The end date is set automatically from the plan's validity (start date = today).
          </p>
        </div>
      </Modal>

      {/* Unassign confirmation */}
      <DeleteConfirmationModal
        isOpen={!!unassignTenant}
        onClose={() => setUnassignTenant(null)}
        onConfirm={() => unassignTenant && unassignMutation.mutate(unassignTenant.tenant_key)}
        title="Unassign Plan"
        description="This removes the assigned plan from the tenant. You can assign a plan again later."
        itemName={unassignTenant ? (unassignTenant.name || unassignTenant.tenant_key) : ''}
        isLoading={unassignMutation.isPending}
      />
    </div>
  );
}
